import React, { useEffect, useState } from 'react';
import { studentAttendanceAPI, masterAPI } from '../services/api';
import { toast } from 'react-toastify';
import { FiCalendar, FiDownload, FiBarChart2, FiUsers } from 'react-icons/fi';

const AttendanceReports = () => {
  const [classes, setClasses] = useState([]);
  const [batches, setBatches] = useState([]);
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('summary'); // 'summary' or 'detailed'
  
  const [filters, setFilters] = useState({
    start_date: new Date(new Date().setDate(1)).toISOString().split('T')[0], // First day of month
    end_date: new Date().toISOString().split('T')[0],
    class_id: '',
    batch_id: ''
  });

  useEffect(() => {
    fetchClasses();
    fetchBatches();
  }, []);

  useEffect(() => {
    if (activeTab === 'summary') {
      fetchSummary();
    }
  }, [filters, activeTab]);

  const fetchClasses = async () => {
    try {
      const response = await masterAPI.getClasses();
      if (response.data.success) {
        setClasses(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching classes:', error);
    }
  };

  const fetchBatches = async () => {
    try {
      const response = await masterAPI.getBatches();
      if (response.data.success) {
        setBatches(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching batches:', error);
    }
  };

  const fetchSummary = async () => {
    setLoading(true);
    try {
      const response = await studentAttendanceAPI.getStudentAttendanceSummary(filters);
      if (response.data.success) {
        setStudents(response.data.data);
      }
    } catch (error) {
      toast.error('Error fetching attendance summary');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  const getAttendanceColor = (percentage) => {
    if (percentage >= 90) return 'text-green-600';
    if (percentage >= 75) return 'text-blue-600';
    if (percentage >= 60) return 'text-yellow-600';
    return 'text-red-600';
  };

  const exportToCSV = () => {
    if (students.length === 0) {
      toast.error('No data to export');
      return;
    }

    const headers = ['Roll No', 'Name', 'Class', 'Batch', 'Present', 'Absent', 'Late', 'Half Day', 'Leave', 'Total Days', 'Attendance %'];
    const rows = students.map(student => [
      student.roll_no || '-',
      `${student.firstname} ${student.lastname}`,
      student.class_name || '-',
      student.batch_name || '-',
      student.present_count,
      student.absent_count,
      student.late_count,
      student.half_day_count,
      student.leave_count,
      student.total_days,
      student.attendance_percentage
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `attendance_report_${filters.start_date}_to_${filters.end_date}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
    toast.success('Report exported successfully');
  };

  return (
    <div className="p-4 md:p-6">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900">Attendance Reports</h1>
          <p className="text-sm md:text-base text-gray-600 mt-1">View and analyze student attendance</p>
        </div>
        <button
          onClick={exportToCSV}
          className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
        >
          <FiDownload /> Export CSV
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm p-4 md:p-6 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <FiCalendar className="inline mr-1" /> Start Date
            </label>
            <input
              type="date"
              name="start_date"
              value={filters.start_date}
              onChange={handleFilterChange}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-600 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <FiCalendar className="inline mr-1" /> End Date
            </label>
            <input
              type="date"
              name="end_date"
              value={filters.end_date}
              onChange={handleFilterChange}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-600 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Class</label>
            <select
              name="class_id"
              value={filters.class_id}
              onChange={handleFilterChange}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-600 focus:border-transparent"
            >
              <option value="">All Classes</option>
              {classes.map(cls => (
                <option key={cls.id} value={cls.id}>{cls.class}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Batch</label>
            <select
              name="batch_id"
              value={filters.batch_id}
              onChange={handleFilterChange}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-600 focus:border-transparent"
            >
              <option value="">All Batches</option>
              {batches.map(batch => (
                <option key={batch.id} value={batch.id}>{batch.batch}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Summary Table */}
      {loading ? (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="text-gray-600 mt-4">Loading report...</p>
        </div>
      ) : students.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-lg">
          <FiBarChart2 size={48} className="mx-auto text-gray-300 mb-4" />
          <h3 className="text-xl font-semibold text-gray-900 mb-2">No Data Found</h3>
          <p className="text-gray-600">No attendance records found for the selected filters</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Roll No</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Student Name</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Class</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Batch</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Present</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Absent</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Late</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Half Day</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Leave</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Total Days</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Attendance %</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {students.map((student) => (
                  <tr key={student.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm text-gray-900">{student.roll_no || '-'}</td>
                    <td className="px-4 py-3 text-sm font-medium text-gray-900">
                      {student.firstname} {student.lastname}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">{student.class_name || '-'}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{student.batch_name || '-'}</td>
                    <td className="px-4 py-3 text-center">
                      <span className="px-2 py-1 text-xs font-medium bg-green-100 text-green-800 rounded">
                        {student.present_count}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className="px-2 py-1 text-xs font-medium bg-red-100 text-red-800 rounded">
                        {student.absent_count}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className="px-2 py-1 text-xs font-medium bg-yellow-100 text-yellow-800 rounded">
                        {student.late_count}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className="px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded">
                        {student.half_day_count}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className="px-2 py-1 text-xs font-medium bg-purple-100 text-purple-800 rounded">
                        {student.leave_count}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center text-sm font-medium text-gray-900">
                      {student.total_days}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className={`text-lg font-bold ${getAttendanceColor(student.attendance_percentage)}`}>
                        {student.attendance_percentage}%
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default AttendanceReports;
